import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import {
  getBsView, createBsLine, updateBsLine, deleteBsLine, reorderBsLines, renameBsSection,
} from '../api/financials_bs';
import StructureEditor from '../components/StructureEditor';

/* Structure du Bilan — édition des lignes de reporting.
   Déplacée depuis l'onglet « Structure » des États financiers vers Paramètres.
   La structure appartient au client (2026-09-03) : le socle standard n'est
   qu'un gabarit de départ. Chaque ligne se renomme (son identité `std_key` est
   stockée, jamais lue dans le libellé), se déplace, s'ajoute et se supprime,
   sauf les lignes porteuses d'un calcul (liquidités, créances-clients, stocks,
   dettes fournisseurs, acomptes clients, résultat de l'exercice), que le
   serveur refuse de supprimer. Les blocs se renomment aussi.
   Le jeton BFRE marque les lignes du besoin en fonds de roulement (utilisé par
   le hub Liquidités). Structure indépendante de l'année. */
export default function BsStructurePage({ activeTenant, addToast, year }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id || null;
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);

  function reload() {
    if (!tenantId) { setView(null); return; }
    setLoading(true);
    getBsView(tenantId, year)
      .then(r => setView(r.data || null))
      .catch(() => addToast?.(p('Erreur chargement structure Bilan'), 'error'))
      .finally(() => setLoading(false));
  }
  useEffect(reload, [tenantId, year]);   // eslint-disable-line

  async function act(promise, errLabel) {
    const res = await promise;
    if (res.data) reload(); else addToast?.(res.error || errLabel, 'error');
  }

  if (!tenantId) return <p className="page-table-empty">{p('Sélectionne une entreprise.')}</p>;
  if (loading && !view) return <p className="page-table-loading">{t('common:status.loading')}</p>;
  if (!view) return null;

  return (
    <StructureEditor
      bare
      columns={[
        { label: p('Actif'), codes: ['current_assets', 'fixed_assets'] },
        { label: p('Passif'), codes: ['current_liabilities', 'long_term_liabilities', 'equity'] },
        { label: p('Autres groupes'), codes: ['bfr'] },
      ]}
      virtualGroups={[
        { code: 'bfr', label: p("Besoin en fonds de roulement d'exploitation (BFRE)"), match: (l) => l.is_bfre },
      ]}
      sections={view.sections || []}
      lines={view.lines || []}
      isProtected={(l) => !l.deletable}
      isLabelLocked={(l) => l.renamable === false}
      onCreate={(section, label) => act(createBsLine(tenantId, section, label), p('Erreur création ligne'))}
      onRename={(id, label) => act(updateBsLine(tenantId, id, { label }), p('Erreur renommage'))}
      onDelete={(id) => act(deleteBsLine(tenantId, id), p('Erreur suppression'))}
      onReorder={(ids) => act(reorderBsLines(tenantId, ids), p('Erreur réordonnancement'))}
      onRenameSection={(code, label) => act(renameBsSection(tenantId, code, label), p('Erreur renommage bloc'))}
      renderTag={(l) => (l.line_role
        ? <span className="fstruct-roletag">{l.line_role === 'vat' ? p('TVA') : p('Assurances sociales')}</span>
        : null)}
      renderActions={(l) => {
        /* RÔLE D'ANCRAGE (2026-08-17) : désigne la ligne qui reçoit un calcul
           externe — décompte TVA, charges sociales. Le libellé ne sert pas
           d'identité (un client peut ranger sa TVA dans « Dettes fiscales »).
           Unique par client, contrôlé côté serveur. */
        const roleSel = (l.section === 'current_liabilities' || l.section === 'current_assets') ? (
          <select
            key="role"
            className="fstruct-revtype"
            title={p('Rôle d’ancrage : cette ligne reçoit le calcul correspondant')}
            value={l.line_role || ''}
            onChange={(e) => act(updateBsLine(tenantId, l.id, { line_role: e.target.value || null }), p('Erreur rôle'))}
          >
            <option value="">{p('— rôle')}</option>
            <option value="vat">{p('TVA')}</option>
            <option value="social">{p('Assurances sociales')}</option>
          </select>
        ) : null;
        // BFRE = besoin en fonds de roulement d'EXPLOITATION → uniquement les
        // actifs / passifs circulants. Pas de jeton sur immobilisations, dettes
        // à long terme ni fonds propres (hors périmètre du BFRE).
        if (l.section !== 'current_assets' && l.section !== 'current_liabilities') return roleSel;
        const mandatory = l.bfr_mandatory;
        // Navy = composante obligatoire figée · Vert (sage) = ajout supplémentaire.
        const extra = l.is_bfre && !mandatory;
        return (
          <>
            {roleSel}
            <button
              type="button"
              className={`fstruct-bfre${l.is_bfre ? ' is-on' : ''}${mandatory ? ' is-locked' : ''}${extra ? ' is-extra' : ''}`}
              disabled={mandatory}
              title={mandatory ? p('Composante BFRE obligatoire') : (l.is_bfre ? p('Retirer du BFRE') : p('Ajouter au BFRE'))}
              onClick={() => { if (!mandatory) act(updateBsLine(tenantId, l.id, { is_bfre: !l.is_bfre }), p('Erreur BFRE')); }}
            >BFRE</button>
          </>
        );
      }}
    />
  );
}
